"use client";

import React, { forwardRef, useMemo, useState, useEffect, useCallback } from "react";
import { cn } from "~/lib/utils";
import { ArrowsUpDownIcon, SearchIcon } from "~/components/icons";
import { Input, InputGroup, InputLeftContent } from "./input";
import { ExportButton } from "./export-button";
import { exportTable, type ExportFormat, type ExportColumn } from "~/lib/table-export";
import { useToast } from "./toast";

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  searchable?: boolean;
  exportable?: boolean;
  render?: (row: T) => React.ReactNode;
  exportRender?: (row: T) => string | number | null;
  className?: string;
  /** Hide this column on mobile screens */
  hideOnMobile?: boolean;
}

export interface EnhancedDataTableProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  columns: Column<T>[];
  data: T[];
  title?: string;
  searchPlaceholder?: string;
  exportFilename?: string;
  showSearch?: boolean;
  showExport?: boolean;
  onSort?: (key: string, direction: "asc" | "desc") => void;
  emptyMessage?: string;
  isLoading?: boolean;
  startItem?: number;
  endItem?: number;
  totalCount?: number;
  onSearchChange?: (searchText: string) => void;
  searchDebounceMs?: number;
  searchValue?: string;
  resultsTitle?: string;
  actionButton?: React.ReactNode;
}

function EnhancedDataTableInner<T>(
  {
    className,
    columns,
    data,
    title,
    searchPlaceholder = "Search table...",
    exportFilename = "export",
    showSearch = true,
    showExport = true,
    onSort,
    emptyMessage = "No data available",
    isLoading = false,
    startItem,
    endItem,
    totalCount,
    onSearchChange,
    searchDebounceMs = 400,
    searchValue: controlledSearchValue,
    resultsTitle = "Results",
    actionButton,
    ...props
  }: EnhancedDataTableProps<T> & { actionButton?: React.ReactNode },
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { showToast } = useToast();
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [localSearchTerm, setLocalSearchTerm] = useState(controlledSearchValue ?? "");

  const isServerSideSearch = !!onSearchChange;

  const searchTerm = controlledSearchValue ?? localSearchTerm;

  useEffect(() => {
    if (controlledSearchValue !== undefined) {
      setLocalSearchTerm(controlledSearchValue);
    }
  }, [controlledSearchValue]);

  useEffect(() => {
    if (!isServerSideSearch) return;

    const handler = setTimeout(() => {
      onSearchChange(localSearchTerm);
    }, searchDebounceMs);

    return () => clearTimeout(handler);
  }, [localSearchTerm, onSearchChange, searchDebounceMs, isServerSideSearch]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchTerm(e.target.value);
  }, []);

  const handleSort = (key: string) => {
    const newDirection = sortKey === key && sortDirection === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    if (!isServerSideSearch && searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = data.filter((row) => {
        return columns.some((column) => {
          if (column.searchable === false) return false;

          const value = (row as Record<string, unknown>)[column.key];
          if (value === null || value === undefined) return false;

          if (typeof value === "object") return false;

          const stringValue = typeof value === "string"
            ? value.toLowerCase()
            : typeof value === "number" || typeof value === "boolean"
              ? String(value).toLowerCase()
              : "";

          return stringValue.includes(term);
        });
      });
    }

    if (!sortKey) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aValue = (a as Record<string, unknown>)[sortKey];
      const bValue = (b as Record<string, unknown>)[sortKey];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        const aNum = aValue ? 1 : 0;
        const bNum = bValue ? 1 : 0;
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (typeof aValue === "object" || typeof bValue === "object") {
        return 0;
      }

      const aString = typeof aValue === "string"
        ? aValue
        : typeof aValue === "number" || typeof aValue === "boolean"
          ? String(aValue)
          : "";

      const bString = typeof bValue === "string"
        ? bValue
        : typeof bValue === "number" || typeof bValue === "boolean"
          ? String(bValue)
          : "";

      const comparison = aString.localeCompare(bString);
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, searchTerm, columns, sortKey, sortDirection, isServerSideSearch]);

  const handleExport = async (format: ExportFormat) => {
    const exportColumns: ExportColumn[] = columns
      .filter((col) => col.exportable !== false)
      .map((col) => ({
        header: col.header,
        key: col.key,
      }));

    const exportData = filteredAndSortedData.map((row) => {
      const exportRow: Record<string, unknown> = {};
      columns.forEach((col) => {
        if (col.exportable === false) return;

        if (col.exportRender) {
          exportRow[col.key] = col.exportRender(row);
        } else {
          exportRow[col.key] = (row as Record<string, unknown>)[col.key];
        }
      });
      return exportRow;
    });

    const result = await exportTable(format, {
      filename: exportFilename,
      title,
      columns: exportColumns,
      data: exportData,
    });

    if (result) {
      showToast(result.message, result.success ? "success" : "error");
    }
  };

  const localResultCount = filteredAndSortedData.length;

  const isPaginatedMode = startItem !== undefined && endItem !== undefined && totalCount !== undefined;

  return (
    <div ref={ref} className={cn("flex flex-col gap-3 sm:gap-4", className)} {...props}>
      {(showSearch || showExport || actionButton) && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          {showSearch && (
            <div className="w-full sm:max-w-md">
              <InputGroup>
                <InputLeftContent>
                  <SearchIcon className="h-4 w-4 text-[#60646c]" />
                </InputLeftContent>
                <Input
                  placeholder={searchPlaceholder}
                  size="md"
                  className="pl-9"
                  value={localSearchTerm}
                  onChange={handleSearchInputChange}
                />
              </InputGroup>
            </div>
          )}

          <div className="flex items-center gap-4 sm:ml-auto">
            {showExport && <ExportButton onExport={handleExport} />}
            {actionButton}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-lg text-[#1c2024] font-semibold">{resultsTitle}</span>
        {isLoading ? (
          <span className="text-lg leading-[26px] tracking-[-0.04px] text-[#60646c]">Loading...</span>
        ) : isPaginatedMode && totalCount !== undefined && totalCount > 0 ? (
          <span className="flex flex-col text-sm text-[#60646c]">
            {startItem} - {endItem} of {totalCount.toLocaleString()}
          </span>
        ) : (
          <span className="flex flex-col text-sm text-[#60646c]">
            {localResultCount.toLocaleString()} total
          </span>
        )}
      </div>

      <div className="w-full overflow-hidden rounded-md border border-[#e8e8ec] bg-white">
        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="border-b border-[#e8e8ec] bg-[#f9f9fb]">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={cn(
                      "px-2 sm:px-3 py-2 sm:py-3 text-left",
                      column.hideOnMobile && "hidden sm:table-cell",
                      column.className
                    )}
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      {column.sortable && (
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-semibold leading-4 sm:leading-5 text-[#1c2024] hover:text-[#003261]"
                        >
                          <ArrowsUpDownIcon className="h-3 w-3 sm:h-4 sm:w-4 text-[#60646c]" />
                          {column.header}
                        </button>
                      )}
                      {!column.sortable && (
                        <span className="text-xs sm:text-sm font-semibold leading-4 sm:leading-5 text-[#1c2024]">
                          {column.header}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-8 text-center text-sm text-[#60646c]"
                  >
                    {isLoading ? "Loading..." : emptyMessage}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="border-b border-[#e8e8ec] last:border-b-0 hover:bg-[#f9f9fb]"
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          "px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm leading-4 sm:leading-5 text-[#1c2024]",
                          column.hideOnMobile && "hidden sm:table-cell",
                          column.className
                        )}
                      >
                        {column.render
                          ? column.render(row)
                          : ((row as Record<string, unknown>)[column.key] as React.ReactNode)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const EnhancedDataTable = forwardRef(EnhancedDataTableInner) as <
  T
>(
  props: EnhancedDataTableProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof EnhancedDataTableInner>;

export { EnhancedDataTable };
